import DateInput from './DateInput';
/**
 * ProfileCompletionModal — popup that forces patients to complete their
 * profile before they can book an appointment.
 *
 * Props:
 *   open       bool         — show/hide
 *   onClose    () => void   — called when user dismisses (X or "Skip for now")
 *   onSaved    () => void   — called after successful save (reload upstream state)
 *   forced     bool         — when true, hide the Skip button (used by booking)
 *
 * Behaviour:
 *   • Fetches `/api/patients/me` + `/api/patients/me/completeness`.
 *   • Pre-fills any existing fields; highlights missing required fields with a
 *     red asterisk + "Required" hint.
 *   • Save calls `PUT /api/patients/me` then `onSaved()` if no fields remain.
 *   • The ID-number input uses a country-aware label (PESEL / SSN / etc.).
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { X, Save, ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRY_OPTIONS, REQUIRED_FIELD_LABELS } from '../lib/idLabels';

export default function ProfileCompletionModal({ open, onClose, onSaved, forced = false }) {
  const [profile, setProfile] = useState(null);
  const [initial, setInitial] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get('/patients/me').then(r => { setProfile(r.data); setInitial(r.data); }),
      api.get('/patients/me/completeness').then(r => setCompleteness(r.data)),
    ]).catch(() => {});
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const missing = new Set((completeness?.missing) || []);
  // Identity fields lock once they hold a saved value — only the clinic can change them.
  const locked = (k) => !!(initial && initial[k] != null && String(initial[k]).trim());

  const save = async () => {
    setSaving(true);
    try {
      // Always include all editable fields so partial values aren't dropped.
      const r0 = await api.put('/patients/me', {
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        photo_url: profile.photo_url || null,
        date_of_birth: profile.date_of_birth || null,
        sex: profile.sex || null,
        pronoun: profile.pronoun || null,
        phone: profile.phone || null,
        id_number: profile.id_number || null,
        pesel: profile.pesel || null,  // keep the legacy field for back-compat
        address: profile.address || null,
        city: profile.city || null,
        postal_code: profile.postal_code || null,
        country: profile.country || 'PL',
        insurance_provider: profile.insurance_provider || null,
        emergency_contact_name: profile.emergency_contact_name || null,
        emergency_contact_phone: profile.emergency_contact_phone || null,
        emergency_contact_relation: profile.emergency_contact_relation || null,
        consent_gdpr: !!profile.consent_gdpr,
        consent_hipaa: !!profile.consent_hipaa,
      });
      setProfile(r0.data); setInitial(r0.data);
      const r = await api.get('/patients/me/completeness');
      setCompleteness(r.data);
      if (r.data.complete) {
        toast.success('Profile complete — you can now book appointments.');
        onSaved?.();
      } else {
        toast.message(`${r.data.missing.length} field(s) still missing.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg max-w-3xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="profile-completion-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-1">WELCOME</div>
            <h3 className="text-2xl font-heading font-bold inline-flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-brand"/> Complete your profile
            </h3>
            <p className="text-sm text-fg-muted mt-1">
              These details are required by EU/HIPAA medical-record law before we can book your first appointment.
              You only need to fill this out once.
            </p>
          </div>
          {!forced && (
            <button onClick={onClose} className="text-fg-muted hover:text-fg" data-testid="pcm-close"><X className="w-5 h-5"/></button>
          )}
        </div>

        {completeness?.complete ? (
          <div className="my-4 p-4 rounded-lg border border-[#3A664D]/30 bg-[#3A664D]/10 text-sm inline-flex items-center gap-2" data-testid="pcm-complete-banner">
            <CheckCircle2 className="w-5 h-5 text-[#3A664D]"/>
            <span>All set! You can now book appointments.</span>
          </div>
        ) : completeness?.missing?.length ? (
          <div className="my-4 p-3 rounded-lg border border-[#FFB000]/40 bg-[#FFB000]/10 text-sm" data-testid="pcm-missing-banner">
            <div className="inline-flex items-center gap-2 font-medium text-fg">
              <AlertTriangle className="w-4 h-4 text-[#FFB000]"/>
              {completeness.missing.length} required field(s) still empty:
            </div>
            <div className="text-xs text-fg-secondary mt-1.5">
              {completeness.missing.map(k => REQUIRED_FIELD_LABELS[k] || k).join(' · ')}
            </div>
          </div>
        ) : null}

        {profile ? (
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="First name" required={missing.has('first_name')} locked={locked('first_name')}>
              <input className="input" value={profile.first_name || ''} onChange={e => set('first_name', e.target.value)} disabled={locked('first_name')} data-testid="pcm-first-name"/>
            </Field>
            <Field label="Last name" required={missing.has('last_name')} locked={locked('last_name')}>
              <input className="input" value={profile.last_name || ''} onChange={e => set('last_name', e.target.value)} disabled={locked('last_name')} data-testid="pcm-last-name"/>
            </Field>
            <Field label="Date of birth" required={missing.has('date_of_birth')} locked={locked('date_of_birth')}>
              <DateInput value={profile.date_of_birth || ''} onChange={e => set('date_of_birth', e.target.value)} disabled={locked('date_of_birth')} data-testid="pcm-dob"/>
            </Field>
            <Field label="Sex" required={missing.has('sex')} locked={locked('sex')}>
              <select className="input" value={profile.sex || ''} onChange={e => set('sex', e.target.value)} disabled={locked('sex')} data-testid="pcm-sex">
                <option value="">— Select —</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </Field>
            <Field label="Pronoun (optional)">
              <select className="input" value={profile.pronoun || ''} onChange={e => set('pronoun', e.target.value)} data-testid="pcm-pronoun">
                <option value="">— Select —</option>
                {['she/her','he/him','they/them','ze/zir','other','prefer not to say'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Phone" required={missing.has('phone')}>
              <input className="input" value={profile.phone || ''} onChange={e => set('phone', e.target.value)} data-testid="pcm-phone"/>
            </Field>
            <Field label="Country" required={missing.has('country')}>
              <select className="input" value={profile.country || 'PL'} onChange={e => set('country', e.target.value)} data-testid="pcm-country">
                {COUNTRY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Health Care Number" required={missing.has('id_number')} locked={locked('id_number') || locked('pesel')} hint="Public health / insurance card number (NFZ · NHS · Medicare · Krankenkasse etc.)">
              <input
                className="input font-mono"
                value={profile.id_number || profile.pesel || ''}
                onChange={e => set('id_number', e.target.value)}
                disabled={locked('id_number') || locked('pesel')}
                data-testid="pcm-id"
              />
            </Field>
            <Field label="Street address" required={missing.has('address')} full>
              <input className="input" value={profile.address || ''} onChange={e => set('address', e.target.value)} data-testid="pcm-address"/>
            </Field>
            <Field label="City" required={missing.has('city')}>
              <input className="input" value={profile.city || ''} onChange={e => set('city', e.target.value)} data-testid="pcm-city"/>
            </Field>
            <Field label="Postal code" required={missing.has('postal_code')}>
              <input className="input" value={profile.postal_code || ''} onChange={e => set('postal_code', e.target.value)} data-testid="pcm-postal"/>
            </Field>
            <Field label="Insurance provider (optional)">
              <input className="input" value={profile.insurance_provider || ''} onChange={e => set('insurance_provider', e.target.value)}/>
            </Field>
            <Field label="Emergency contact name (optional)">
              <input className="input" value={profile.emergency_contact_name || ''} onChange={e => set('emergency_contact_name', e.target.value)}/>
            </Field>
            <Field label="Emergency contact phone (optional)">
              <input className="input" value={profile.emergency_contact_phone || ''} onChange={e => set('emergency_contact_phone', e.target.value)}/>
            </Field>
          </div>
        ) : (
          <div className="py-10 text-center text-fg-muted text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2"/> Loading your profile…
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-soft mt-5">
          {!forced && (
            <button onClick={() => { sessionStorage.setItem('profile-completion-skip', '1'); onClose(); }} className="btn-secondary text-sm" data-testid="pcm-skip">
              Skip for now
            </button>
          )}
          <button onClick={save} disabled={saving || !profile} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="pcm-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, children, required, hint, full, locked }) {
  return (
    <label className={full ? 'md:col-span-2 block' : 'block'}>
      <div className="text-xs text-fg-muted mb-1 flex items-center gap-1">
        {label}
        {required && <span className="text-[#B83A3A]" title="Required to book appointments">*</span>}
      </div>
      {children}
      {locked && <div className="text-[10px] text-fg-muted mt-1">Locked for record integrity — contact your clinic to change.</div>}
      {hint && !locked && <div className="text-[10px] text-fg-muted mt-1">{hint}</div>}
    </label>
  );
}
